import {
  getAccessToken,
  getCompanyCode,
  getPendingMfaToken,
  isAssignedCompany,
  redirectToLogin,
  refreshAccessToken,
  restoreSession,
  SESSION_OK,
  SESSION_UNAUTHORIZED,
  setAccessToken,
} from "../auth/authSession";

const originalFetch = window.fetch;

let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, tokens = null) => {
  refreshQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(tokens);
  });
  refreshQueue = [];
};

const requestUrl = (input) => {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input?.url || "";
};

const isAuthRefreshUrl = (url = "") =>
  /\/auth\/(login|refresh|ping)\b/.test(url) ||
  /\/api\/(verify-mfa|resend-mfa|register-phone|verify-phone|resend-phone|update-password)\b/.test(url) ||
  /\/logout\b/.test(url);

const isRefreshUrl = (url = "") => /\/auth\/refresh\b/.test(url);

const isProfileUrl = (url = "") => /\/api\/get-profile\b/.test(url);

const shouldLeaveCompany = () => {
  const path = window.location.pathname;
  if (path === "/login" || path === "/company" || path === "/change-password") {
    return false;
  }
  return !isAssignedCompany(getCompanyCode());
};

const handleForbiddenCompanyAccess = async (url) => {
  if (isAuthRefreshUrl(url) || isProfileUrl(url)) {
    return;
  }
  await restoreSession();
  if (shouldLeaveCompany()) {
    window.location.replace("/company");
  }
};

const sanitizeHeaders = (headers) => {
  const next = { ...headers };
  const authorization = next.Authorization || next.authorization;
  if (typeof authorization === "string") {
    const token = authorization.replace(/^Bearer\s+/i, "").trim();
    if (!token || token === "undefined" || token === "null") {
      delete next.Authorization;
      delete next.authorization;
    }
  }
  const bearer = getPendingMfaToken() || getAccessToken();
  if (bearer) {
    next.Authorization = `Bearer ${bearer}`;
  }
  return next;
};

const retryRequest = (input, requestInit, headers, tokens) => {
  const retryHeaders = sanitizeHeaders({
    ...headers,
    ...(tokens?.accessToken
      ? { Authorization: `Bearer ${tokens.accessToken}` }
      : {}),
  });
  return originalFetch(input, {
    ...requestInit,
    headers: retryHeaders,
    credentials: "include",
  });
};

const refreshAuthToken = async () => {
  const result = await refreshAccessToken();
  if (result.status === SESSION_UNAUTHORIZED) {
    redirectToLogin();
    throw new Error("Session expired");
  }
  if (result.status !== SESSION_OK) {
    throw new Error("Unable to refresh session");
  }
  return result.tokens;
};

window.fetch = async (input, init = {}) => {
  const url = requestUrl(input);
 // Bypass Procurement authentication for local scanner service
  if (
    url.startsWith("http://localhost:9090/") ||
    url.startsWith("http://127.0.0.1:9090/")
  ) {
    return originalFetch(input, init);
  }

  const isFormData = init.body instanceof FormData;
  const headers = sanitizeHeaders({
    ...(init.headers || {}),
  });

  if (!isFormData && !headers["Content-Type"] && !headers["content-type"]) {
    headers["Content-Type"] = "application/json";
  }

  // The refresh cookie is the credential. An expired access token on this
  // request would be rejected before the refresh endpoint runs.
  if (isRefreshUrl(url)) {
    delete headers.Authorization;
    delete headers.authorization;
  }

  const requestInit = {
    ...init,
    headers,
    credentials: init.credentials || "include",
  };

  let response = await originalFetch(input, requestInit);

  if (response.status === 403) {
    await handleForbiddenCompanyAccess(requestUrl(input));
    return response;
  }

  if (response.status !== 401) {
    return response;
  }

  if (isAuthRefreshUrl(url)) {
    return response;
  }

  if (isRefreshing) {
    const tokens = await new Promise((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    });
    return retryRequest(input, requestInit, headers, tokens);
  }

  isRefreshing = true;
  try {
    const newTokens = await refreshAuthToken();
    isRefreshing = false;
    processQueue(null, newTokens);
    return retryRequest(input, requestInit, headers, newTokens);
  } catch (err) {
    isRefreshing = false;
    processQueue(err, null);
    throw err;
  }
};

export { setAccessToken };
